import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import { systemChangeLookupApiCall } from "../../../../helpers/fakebackend_helper";
import { ColourChip } from "./shared";

/**
 * System Changes (CRMit Task 24912) - statuses, types and priorities. The three
 * tables are the same shape, so one table component serves all three.
 *
 * Status "Done" (FlagComplete) is what puts a change on the Ready to bundle tab.
 * Inactive rows drop out of the pickers but stay on the changes that use them.
 */
const KINDS = {
  status: { label: "Statuses", single: "status", listKey: "systemchangestatus_list", hasDone: true },
  type: { label: "Types", single: "type", listKey: "systemchangetype_list" },
  priority: { label: "Priorities", single: "priority", listKey: "systemchangepriority_list" },
};

const EMPTY_FORM = {
  Name: "",
  Description: "",
  CssColourBackground: "",
  CssColourText: "",
  Order: "",
  FlagActive: 1,
  FlagComplete: 0,
};

const LookupsTab = ({ onChanged }) => {
  const [kind, setKind] = useState("status");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  const config = KINDS[kind];

  const load = async () => {
    setLoading(true);
    try {
      const response = await systemChangeLookupApiCall(kind, "list");
      setRows(response?.[config.listKey] || []);
    } catch (e) {
      toast.error(`Could not load ${config.label.toLowerCase()}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    setKeyword("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const openForm = (row) => {
    setEditId(row?.Id || null);
    setForm(
      row
        ? Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [key, row[key] ?? EMPTY_FORM[key]]))
        : EMPTY_FORM
    );
    setFormOpen(true);
  };

  const saveForm = async () => {
    const payload = { ...form };
    if (!config.hasDone) delete payload.FlagComplete;
    try {
      const response = await systemChangeLookupApiCall(kind, editId ? "update" : "create", [
        { ...(editId && { Id: editId }), ...payload },
      ]);
      if (response?.status === 200) {
        toast.success("Saved");
        setFormOpen(false);
        load();
        onChanged?.();
      } else {
        toast.error(response?.message || "Could not save");
      }
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await systemChangeLookupApiCall(kind, "delete", { Id: deleteRow.Id });
      if (response?.status === 200) {
        toast.success("Deleted");
        setDeleteRow(null);
        load();
        onChanged?.();
      } else {
        toast.error(response?.message || "Could not delete");
      }
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const term = keyword.trim().toLowerCase();
  const gridRows = rows
    .filter((row) => !term || String(row.Name || "").toLowerCase().includes(term))
    .map((row) => ({ ...row, id: row.Id }));

  const columns = [
    { field: "Id", headerName: "Id", width: 70 },
    {
      field: "Name",
      headerName: "Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <ColourChip
          label={params.value}
          background={params.row.CssColourBackground}
          colour={params.row.CssColourText}
        />
      ),
    },
    { field: "Order", headerName: "Order", width: 80, type: "number" },
    {
      field: "FlagActive",
      headerName: "Active",
      width: 80,
      renderCell: (params) => (Number(params.value) === 1 ? "Yes" : "No"),
    },
    ...(config.hasDone
      ? [
          {
            field: "FlagComplete",
            headerName: "Done",
            width: 80,
            renderCell: (params) => (Number(params.value) === 1 ? "Yes" : "No"),
          },
        ]
      : []),
    { field: "CountInUse", headerName: "Changes", width: 95, type: "number" },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openForm(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              Number(params.row.CountInUse) > 0
                ? "In use - make it inactive instead"
                : "Delete"
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={Number(params.row.CountInUse) > 0}
                onClick={() => setDeleteRow(params.row)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(e, value) => value && setKind(value)}
          >
            {Object.entries(KINDS).map(([key, value]) => (
              <ToggleButton key={key} value={key}>
                {value.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder={`Search ${config.label.toLowerCase()}...`}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>
          Create {config.single}
        </Button>
      </Box>

      <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      <AppModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={`${editId ? "Edit" : "Create"} ${config.single}`}
        onSubmit={saveForm}
        submitDisabled={!String(form.Name || "").trim()}
      >
        <TextField
          label="Name"
          value={form.Name}
          onChange={(e) => setForm({ ...form, Name: e.target.value })}
          fullWidth
          autoFocus
          required
        />
        <TextField
          label="Description"
          value={form.Description || ""}
          onChange={(e) => setForm({ ...form, Description: e.target.value })}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mt: 2 }}>
          <TextField
            type="color"
            label="Background"
            value={form.CssColourBackground || "#ffffff"}
            onChange={(e) => setForm({ ...form, CssColourBackground: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="color"
            label="Text"
            value={form.CssColourText || "#000000"}
            onChange={(e) => setForm({ ...form, CssColourText: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="number"
            label="Order"
            value={form.Order ?? ""}
            onChange={(e) => setForm({ ...form, Order: e.target.value })}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(form.FlagActive) === 1}
                onChange={(e) => setForm({ ...form, FlagActive: e.target.checked ? 1 : 0 })}
              />
            }
            label="Active (offered in the pickers)"
          />
          {config.hasDone && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Number(form.FlagComplete) === 1}
                  onChange={(e) => setForm({ ...form, FlagComplete: e.target.checked ? 1 : 0 })}
                />
              }
              label="Done (ready to bundle)"
            />
          )}
        </Box>
      </AppModal>

      <AppModal
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        title={`Delete ${config.single} "${deleteRow?.Name || ""}"?`}
        onSubmit={confirmDelete}
        submitLabel="Delete"
        submitColor="error"
      >
        No change uses it, so nothing else is affected.
      </AppModal>
    </>
  );
};

export default LookupsTab;
